import React, { useState, useEffect } from 'react';
import { Play, Pause, Music, Keyboard, ChevronDown } from 'lucide-react';
import { CATEGORIES } from './constants';

export const InstrumentPicker = ({ 
  track, 
  onSelect, 
  userSamples, 
  instrumentPackages = [], 
  loadingInstrument, 
  isAuthenticated = false, 
  authLoading = false 
}) => {
  const [previewAudio, setPreviewAudio] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const API = process.env.REACT_APP_BACKEND_URL || '';
  
  const previewSample = (id, url) => {
    // Stop current preview
    if (previewAudio) {
      previewAudio.pause();
      previewAudio.currentTime = 0;
    }
    
    // If clicking same sample, just stop
    if (playingId === id) {
      setPlayingId(null);
      setPreviewAudio(null);
      return;
    }
    
    // Play new sample
    const fullUrl = url.startsWith('http') ? url : `${API}${url}`;
    const audio = new Audio(fullUrl);
    audio.volume = 0.7;
    audio.onended = () => {
      setPlayingId(null);
      setPreviewAudio(null);
    };
    audio.onerror = (e) => {
      console.error('Preview error:', e);
      setPlayingId(null);
      setPreviewAudio(null);
    };
    audio.play().catch(e => {
      console.error('Play error:', e);
      setPlayingId(null);
    });
    
    setPreviewAudio(audio);
    setPlayingId(id);
  };
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (previewAudio) {
        previewAudio.pause();
      }
    };
  }, [previewAudio]);
  
  const [expandedSection, setExpandedSection] = useState('kits'); // 'kits', 'samples', or category name
  
  // Check auth - use state to avoid SSR/hydration issues
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);
  const [hasLocalToken, setHasLocalToken] = useState(true); // Default true to avoid flash of warning
  
  useEffect(() => {
    // Check localStorage after mount to avoid SSR issues
    const token = localStorage.getItem('auth_token');
    setHasLocalToken(!!token);
    setHasCheckedAuth(true);
  }, []);
  
  // Don't show warning if: still loading auth, OR context says authenticated, OR localStorage has token, OR we haven't checked yet
  const shouldShowWarning = hasCheckedAuth && !authLoading && !isAuthenticated && !hasLocalToken;
  
  return (
    <div className="p-2 max-h-80 overflow-y-auto">
      {/* Auth status warning - only show after full auth check */}
      {shouldShowWarning && (
        <div className="mb-3 p-2 bg-red-500/20 border border-red-500 rounded-lg text-red-300 text-xs">
          Warning: Not logged in - Please log in at /admin first
        </div>
      )}
      
      {/* Quick Select Dropdown for Instrument Kits */}
      {instrumentPackages.length > 0 ? (
        <div className="mb-3">
          <button 
            onClick={() => setExpandedSection(expandedSection === 'kits' ? null : 'kits')}
            className="w-full flex items-center justify-between px-3 py-2 bg-purple-500/20 rounded-lg text-purple-300 hover:bg-purple-500/30 border border-purple-500/50"
          >
            <div className="flex items-center gap-2">
              <Keyboard className="w-4 h-4" />
              <span className="text-sm font-medium">Instrument Kits</span>
              <span className="text-xs bg-purple-500/30 px-1.5 py-0.5 rounded">{instrumentPackages.length}</span>
            </div>
            <ChevronDown className={`w-4 h-4 transition-transform ${expandedSection === 'kits' ? 'rotate-180' : ''}`} />
          </button>
          
          {expandedSection === 'kits' && (
            <div className="mt-1 space-y-1 pl-2">
              {instrumentPackages.map(pkg => (
                <button 
                  key={pkg.id} 
                  onClick={() => onSelect(`package:${pkg.id}`)} 
                  className={`w-full text-left px-3 py-2 rounded-lg text-xs hover:bg-purple-500/20 flex items-center gap-2 ${track.packageId === pkg.id ? 'bg-purple-500/30 text-purple-300 border border-purple-500' : 'text-gray-300 bg-[#1a1a1a]'}`}
                  data-testid={`package-${pkg.id}`}
                >
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: pkg.color || '#8b5cf6' }} />
                  <span className="flex-1 font-medium">{pkg.name}</span>
                  <span className="text-[10px] text-gray-500">{pkg.subInstruments?.length || 0} pads</span>
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (hasLocalToken || isAuthenticated) ? (
        <div className="mb-3 p-2 bg-yellow-500/20 border border-yellow-500/50 rounded-lg text-yellow-300 text-xs">
          Loading Instrument Kits... If this persists, click Packages button above to reload.
        </div>
      ) : null}
      
      {/* My Samples Dropdown */}
      {Object.keys(userSamples).length > 0 ? (
        <div className="mb-3">
          <button 
            onClick={() => setExpandedSection(expandedSection === 'samples' ? null : 'samples')}
            className="w-full flex items-center justify-between px-3 py-2 bg-orange-500/20 rounded-lg text-orange-300 hover:bg-orange-500/30 border border-orange-500/50"
          >
            <div className="flex items-center gap-2">
              <Music className="w-4 h-4" />
              <span className="text-sm font-medium">My Samples</span>
              <span className="text-xs bg-orange-500/30 px-1.5 py-0.5 rounded">{Object.keys(userSamples).length}</span>
            </div>
            <ChevronDown className={`w-4 h-4 transition-transform ${expandedSection === 'samples' ? 'rotate-180' : ''}`} />
          </button>
          
          {expandedSection === 'samples' && (
            <div className="mt-1 space-y-1 pl-2">
              {Object.entries(userSamples).map(([id, sample]) => (
                <div key={id} className={`flex items-center gap-1 px-3 py-2 rounded-lg text-xs hover:bg-orange-500/10 ${track.instrumentId === `user:${id}` ? 'bg-orange-500/20 text-orange-400 border border-orange-500' : 'text-gray-300 bg-[#1a1a1a]'}`}>
                  <button 
                    onClick={() => previewSample(id, sample.url)} 
                    className={`p-1.5 rounded hover:bg-white/20 ${playingId === id ? 'text-green-400 bg-green-400/20' : 'text-gray-400 hover:text-green-400'}`}
                    title={playingId === id ? 'Stop Preview' : 'Preview Sample'}
                    data-testid={`preview-${id}`}
                  >
                    {playingId === id ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                  </button>
                  <button onClick={() => onSelect(`user:${id}`)} className="flex-1 text-left truncate font-medium" data-testid={`sample-${id}`}>
                    {sample.name}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (hasLocalToken || isAuthenticated) ? (
        <div className="mb-3 p-2 bg-yellow-500/20 border border-yellow-500/50 rounded-lg text-yellow-300 text-xs">
          Loading My Samples...
        </div>
      ) : null}
      
      {/* Standard Instruments */}
      <div className="border-t border-[#333] pt-2">
        <div className="text-[10px] text-gray-500 uppercase px-2 py-1 mb-1">Standard Instruments</div>
        {Object.entries(CATEGORIES).map(([cat, insts]) => (
          <div key={cat} className="mb-2">
            <button 
              onClick={() => setExpandedSection(expandedSection === cat ? null : cat)}
              className="w-full flex items-center justify-between px-2 py-1 text-[10px] text-gray-400 hover:text-white uppercase"
            >
              {cat}
              <ChevronDown className={`w-3 h-3 transition-transform ${expandedSection === cat ? 'rotate-180' : ''}`} />
            </button>
            {expandedSection === cat && insts.map(inst => (
              <button key={inst.id} onClick={() => onSelect(inst.id)} className={`w-full text-left px-3 py-1.5 rounded text-xs hover:bg-white/10 flex items-center gap-2 ${track.instrumentId === inst.id ? 'bg-blue-500/20 text-blue-400' : 'text-gray-300'}`}>
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: inst.color }} />
                {inst.name}
                {loadingInstrument === inst.id && <span className="ml-auto text-blue-400 text-[10px]">loading...</span>}
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default InstrumentPicker;
